// frontend/src/components/Seo.js
//
// Single source of truth for per-page <head> metadata.
//
// Why this exists: index.html used to hardcode ONE title/description for the
// whole SPA, so every route — homepage, /plans, /blog and every article —
// served identical metadata. Google indexed /post-scheduling under the
// homepage's title because it had nothing else to go on.
//
// Rendered once in Layout.js (driven by src/data/seoMeta.js) and again, more
// specifically, by pages with dynamic content such as BlogPost. Helmet's
// last-mounted-wins semantics mean the deeper page-level <Seo> overrides the
// Layout default without any extra wiring.

import React from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'

// Constants live in data/seoMeta.js (backed by seo-meta.json) so the build-time
// prerender script and the React runtime share one definition.
// Imported for use below AND re-exported, because pages already import them from
// this module — `export ... from` alone would not bring them into scope here.
import { SITE_URL, BRAND, DEFAULT_OG } from '../data/seoMeta'

export { SITE_URL, BRAND, DEFAULT_OG }

export default function Seo ({
  title,                     // page title WITHOUT the brand suffix
  description,
  path,                      // canonical path, e.g. '/plans'
  image        = DEFAULT_OG,
  type         = 'website',
  noindex      = false,
  publishedAt,               // articles only (ISO date)
  modifiedAt,                // articles only (ISO date)
  author,                    // articles only
  jsonLd,                    // object or array of JSON-LD nodes
}) {
  // Avoid "Greatly Social | Greatly Social" if a caller passes a branded title.
  const fullTitle = !title
    ? BRAND
    : title.includes(BRAND)
      ? title
      : `${title} | ${BRAND}`

  const canonical = path
    ? `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
    : undefined

  const absoluteImage = image?.startsWith('http') ? image : `${SITE_URL}${image}`

  const nodes = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : []

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name='description' content={description} />}
      {canonical && <link rel='canonical' href={canonical} />}
      {noindex && <meta name='robots' content='noindex,nofollow' />}

      {/* Open Graph */}
      <meta property='og:site_name'   content={BRAND} />
      <meta property='og:title'       content={fullTitle} />
      {description && <meta property='og:description' content={description} />}
      <meta property='og:type'        content={type} />
      <meta property='og:image'       content={absoluteImage} />
      {/* Facebook warns and may drop the image on first scrape without explicit
          dimensions. Only emitted for our own cover, whose size we know — blog
          hero images are third-party URLs of unknown size. */}
      {absoluteImage === DEFAULT_OG && <meta property='og:image:width'  content='1200' />}
      {absoluteImage === DEFAULT_OG && <meta property='og:image:height' content='630' />}
      <meta property='og:image:alt' content={`${BRAND} — ${title || 'social media scheduling'}`} />
      {canonical && <meta property='og:url' content={canonical} />}

      {/* Article-specific OG */}
      {publishedAt && <meta property='article:published_time' content={publishedAt} />}
      {modifiedAt  && <meta property='article:modified_time'  content={modifiedAt} />}
      {author      && <meta property='article:author'         content={author} />}

      {/* Twitter */}
      <meta name='twitter:card'        content='summary_large_image' />
      <meta name='twitter:site'        content='@GreatlySocial' />
      <meta name='twitter:title'       content={fullTitle} />
      {description && <meta name='twitter:description' content={description} />}
      <meta name='twitter:image'       content={absoluteImage} />

      {nodes.map((node, i) => (
        <script type='application/ld+json' key={i}>
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  )
}
